import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { ListGroup, Button, Spinner } from 'react-bootstrap';
import { FaTrash, FaEdit, FaChevronLeft, FaChevronRight, FaGift } from 'react-icons/fa';
import { onSnapshot } from 'firebase/firestore';
import './BenefitList.css';
import ContentUnitTag from '../common/ContentUnitTag';
import { useUser } from '../../context/UserContext';
import { useTenant } from '../../context/TenantContext';
import { useMembership } from '../../context/MembershipContext';
import { usePaginatedTenantList } from '../../hooks/usePaginatedTenantList';
import { LAST_READ_MODULES } from '../../platform/lastRead/lastReadModules';
import { useUnreadItemIds } from '../../platform/lastRead/useUnreadItemIds';
import { isPinnedActive, sortPinnedItems } from '../../platform/pinnedContent';
import PinnedHeroCard, { PinnedHeroSection } from '../../platform/PinnedHeroCard';
import ContentListFeedSection from '../../platform/ContentListFeedSection';
import { useConfirm } from '../../platform/confirm/ConfirmProvider';
import { useToast } from '../../platform/toast/ToastProvider';
import { fetchUnitNameMap } from '../../services/unitsService';
import { getVisibleUnitIds } from '../../services/contentQueryHelpers';
import { canViewAllUnitsForTenant } from '../../services/tenantListQuery';
import { filterByActiveTenant } from '../../services/tenantContentFilter';
import {
  BENEFITS_PAGE_SIZE,
  buildBenefitsPageQuery,
  buildPinnedBenefitsQuery,
  deleteBenefit,
  fetchBenefitsPage,
  mapBenefit,
} from '../../services/benefitsService';
import { plainTextExcerpt } from '../../platform/contentExcerpt';
import {
  PREMIUM_MODULE_IDS,
  premiumListClassName,
  showListNavigateHint,
} from '../../platform/contentListVisual';
import ListItemNavigateHint from '../../platform/ListItemNavigateHint';

async function fetchBenefitsPageItems(user, tenantId, memberships, options) {
  const { benefits, lastVisible } = await fetchBenefitsPage(user, tenantId, memberships, options);
  return { items: benefits, lastVisible };
}

function BenefitList({ onSelectBenefit, isAdmin, isLocalAdmin, onEditBenefit }) {
  const [unitNames, setUnitNames] = useState({});
  const [pinnedBenefits, setPinnedBenefits] = useState([]);

  const listTopRef = useRef(null);

  const { user, loading } = useUser();
  const { tenantId } = useTenant();
  const { memberships } = useMembership();

  const {
    items: benefits,
    pageIndex,
    hasNextPage,
    pageLoading,
    goToPrevPage,
    goToNextPage,
    pageSize,
  } = usePaginatedTenantList({
    tenantId,
    user,
    memberships,
    userLoading: loading,
    pageSize: BENEFITS_PAGE_SIZE,
    buildPageQuery: buildBenefitsPageQuery,
    fetchPage: fetchBenefitsPageItems,
    mapDoc: mapBenefit,
    logLabel: 'benefits',
  });

  useEffect(() => {
    if (loading || !user || !tenantId) {
      setPinnedBenefits([]);
      return undefined;
    }

    const pinnedQuery = buildPinnedBenefitsQuery(tenantId);
    const unsubscribe = onSnapshot(pinnedQuery, (querySnapshot) => {
      const items = filterByActiveTenant(
        querySnapshot.docs.map(mapBenefit),
        tenantId,
      )
        .filter((benefit) => isPinnedActive(benefit))
        .filter((benefit) => {
          if (canViewAllUnitsForTenant(user, memberships, tenantId)) {
            return true;
          }
          return getVisibleUnitIds(user).includes(benefit.unit);
        });

      setPinnedBenefits(sortPinnedItems(items));
    }, (error) => {
      console.error('Error fetching pinned benefits:', error);
      setPinnedBenefits([]);
    });

    return () => unsubscribe();
  }, [loading, user, tenantId, memberships]);

  const pinnedHeroIds = useMemo(
    () => new Set(pinnedBenefits.map((item) => item.id)),
    [pinnedBenefits],
  );

  const regularBenefits = useMemo(
    () => benefits.filter((benefit) => !pinnedHeroIds.has(benefit.id)),
    [benefits, pinnedHeroIds],
  );

  const unreadBenefitIds = useUnreadItemIds(
    LAST_READ_MODULES.BENEFITS,
    [...pinnedBenefits, ...regularBenefits],
  );

  const scrollListIntoView = useCallback(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    fetchUnitNameMap(tenantId).then(setUnitNames);
  }, [tenantId]);

  const confirm = useConfirm();
  const toast = useToast();

  const handleDelete = async (benefitId) => {
    const confirmed = await confirm({
      title: 'Izbriši ugodnost',
      message: 'Ali ste prepričani, da želite izbrisati to ugodnost?',
      confirmLabel: 'Izbriši',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteBenefit(benefitId);
      toast.deleted('Ugodnost je bila izbrisana.');
    } catch (error) {
      console.error('Error deleting benefit:', error);
      toast.error('Ugodnosti ni bilo mogoče izbrisati.');
    }
  };

  const handlePrevPage = () => {
    if (goToPrevPage()) {
      scrollListIntoView();
    }
  };

  const handleNextPage = () => {
    if (goToNextPage()) {
      scrollListIntoView();
    }
  };

  const formatDate = (date) =>
    date?.toLocaleString('sl-SI', {
      day: 'numeric',
      month: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }) ?? '';

  const showPinnedSection = pageIndex === 0 && pinnedBenefits.length > 0;
  const hasAnyItems = pinnedBenefits.length > 0 || regularBenefits.length > 0;
  const rangeStart = regularBenefits.length ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = pageIndex * pageSize + regularBenefits.length;
  const showPagination = pageIndex > 0 || hasNextPage;
  const reserveActionsColumn = Boolean(user?.uid);
  const showNavigateHint = showListNavigateHint(PREMIUM_MODULE_IDS.BENEFITS);
  const listClassName = premiumListClassName(
    PREMIUM_MODULE_IDS.BENEFITS,
    'benefit-list platform-content-list',
  );
  const wrapperClassName = [
    'content-list-wrapper',
    'platform-content-list-wrapper',
    showNavigateHint && 'platform-content-list-wrapper--card-stack',
  ].filter(Boolean).join(' ');

  const renderActions = (benefit, canEdit) => {
    if (!reserveActionsColumn) return null;

    return (
      <div className="benefit-list-item-aside platform-content-list-item-aside">
        <div className="platform-content-list-item-aside-inner">
          {canEdit ? (
            <div className="benefit-list-item-actions platform-content-list-item-actions">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditBenefit(benefit);
                }}
              >
                <FaEdit /> Uredi
              </Button>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(benefit.id);
                }}
              >
                <FaTrash /> Izbriši
              </Button>
            </div>
          ) : null}
          {showNavigateHint ? <ListItemNavigateHint /> : null}
        </div>
      </div>
    );
  };

  const renderThumb = (benefit, isUnread) => (
    <div className="platform-content-list-item-thumb">
      {isUnread && (
        <span className="platform-content-list-item-unread-badge">
          <span
            className="platform-unread-dot"
            title="Neprebrano"
            aria-label="Neprebrano"
          />
        </span>
      )}
      {benefit.thumbnailUrl ? (
        <img
          src={benefit.thumbnailUrl}
          alt=""
          loading="lazy"
        />
      ) : (
        <div className="platform-content-list-item-thumb-placeholder" aria-hidden="true">
          <FaGift />
        </div>
      )}
    </div>
  );

  const renderPinnedCard = (benefit) => {
    const canEdit = isAdmin || isLocalAdmin || (user && user.uid === benefit.uid);
    const isUnread = unreadBenefitIds.includes(benefit.id);

    return (
      <PinnedHeroCard
        key={benefit.id}
        pinnedUntil={benefit.pinnedUntil}
        thumbnailUrl={benefit.thumbnailUrl}
        placeholderIcon={FaGift}
        title={benefit.title}
        isUnread={isUnread}
        onClick={() => onSelectBenefit(benefit)}
        badges={<ContentUnitTag item={benefit} unitNames={unitNames} />}
        meta={(
          <>
            <span>{benefit.displayName || 'Anonimen'}</span>
            <span className="benefit-list-item-separator">·</span>
            <span>{formatDate(benefit.createdAt)}</span>
          </>
        )}
        showActionsArea={reserveActionsColumn}
        actions={canEdit ? (
          <>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEditBenefit(benefit);
              }}
            >
              <FaEdit /> Uredi
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(benefit.id);
              }}
            >
              <FaTrash /> Izbriši
            </Button>
          </>
        ) : null}
        actionsLayout={canEdit ? 'flex' : 'reserved'}
      />
    );
  };

  const renderRegularItem = (benefit) => {
    const canEdit = isAdmin || isLocalAdmin || (user && user.uid === benefit.uid);
    const isUnread = unreadBenefitIds.includes(benefit.id);
    const excerpt = plainTextExcerpt(benefit.text);

    return (
      <ListGroup.Item
        key={benefit.id}
        className={`benefit-list-item platform-content-list-item${isUnread ? ' benefit-list-item--unread platform-content-list-item--unread' : ''}`}
        onClick={() => onSelectBenefit(benefit)}
      >
        <div
          className={`benefit-list-item-main platform-content-list-item-main${reserveActionsColumn ? ' benefit-list-item-main--has-actions platform-content-list-item-main--has-actions' : ''}`}
        >
          <div className="benefit-list-item-content platform-content-list-item-content">
            {renderThumb(benefit, isUnread)}
            <div className="benefit-list-item-body platform-content-list-item-body">
              <div className="benefit-list-item-title platform-content-list-item-title">{benefit.title}</div>
              <div className="benefit-list-item-badges platform-content-list-item-badges">
                <ContentUnitTag item={benefit} unitNames={unitNames} />
              </div>
              {excerpt && (
                <p className="platform-content-list-item-excerpt platform-content-list-item-excerpt--premium">
                  {excerpt}
                </p>
              )}
              <div className="benefit-list-item-footer platform-content-list-item-footer">
                <span>Ustvaril {benefit.displayName || 'Anonimen'}</span>
                <span className="benefit-list-item-separator">·</span>
                <span>{formatDate(benefit.createdAt)}</span>
              </div>
            </div>
          </div>
          {renderActions(benefit, canEdit)}
        </div>
      </ListGroup.Item>
    );
  };

  if (loading) {
    return (
      <div className="platform-content-list-loading">
        <Spinner animation="border" role="status" size="sm">
          <span className="visually-hidden">Nalaganje...</span>
        </Spinner>
      </div>
    );
  }

  if (!pageLoading && !hasAnyItems && pageIndex === 0) {
    return (
      <div className={`platform-content-list-empty${showNavigateHint ? ' platform-content-list-empty--premium' : ''}`}>
        <strong>Ni ugodnosti</strong>
        <span>Trenutno ni objavljenih ugodnosti za prikaz.</span>
      </div>
    );
  }

  return (
    <div className={wrapperClassName} ref={listTopRef}>
      {pageLoading && (
        <div className="platform-content-list-loading platform-content-list-loading--overlay">
          <Spinner animation="border" role="status" size="sm">
            <span className="visually-hidden">Nalaganje...</span>
          </Spinner>
        </div>
      )}

      {!pageLoading && showPinnedSection && (
        <PinnedHeroSection
          title="Pripete ugodnosti"
          count={pinnedBenefits.length}
          ariaLabel="Pripete ugodnosti"
        >
          {pinnedBenefits.map(renderPinnedCard)}
        </PinnedHeroSection>
      )}

      {!pageLoading && (regularBenefits.length > 0 || pageIndex > 0) && (
        <ContentListFeedSection
          heading={showPinnedSection && regularBenefits.length > 0 ? 'Ostale ugodnosti' : null}
          ariaLabel="Seznam ugodnosti"
        >
          <ListGroup className={listClassName}>
            {regularBenefits.map(renderRegularItem)}
          </ListGroup>
        </ContentListFeedSection>
      )}

      {showPagination && (
        <nav className="platform-content-list-pagination" aria-label="Strani ugodnosti">
          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handlePrevPage}
            disabled={pageIndex === 0 || pageLoading}
          >
            <FaChevronLeft aria-hidden="true" />
            <span>Prejšnja</span>
          </Button>

          <div className="platform-content-list-pagination__info">
            <span className="platform-content-list-pagination__page">Stran {pageIndex + 1}</span>
            {regularBenefits.length > 0 && (
              <span className="platform-content-list-pagination__range">
                Prikazano {rangeStart}–{rangeEnd}
              </span>
            )}
          </div>

          <Button
            variant="outline-secondary"
            size="sm"
            className="platform-content-list-pagination__btn"
            onClick={handleNextPage}
            disabled={!hasNextPage || pageLoading}
          >
            <span>Naslednja</span>
            <FaChevronRight aria-hidden="true" />
          </Button>
        </nav>
      )}
    </div>
  );
}

BenefitList.defaultProps = {
  onEditBenefit: () => {},
};

export default BenefitList;
